import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useDocument, useDocumentStatus, useExtractedKpis, useSavedKpis } from '../hooks/useDocSenseData';
import ProcessingTracker from './ProcessingTracker';
import KpiReviewTable from './KpiReviewTable';

const DocumentDetailPage = ({ documentId, onBack }) => {
  const { data: doc, isLoading: docLoading } = useDocument(documentId);

  const isProcessing = doc && !['COMPLETED', 'FAILED'].includes(doc.status);
  const { status: polledStatus } = useDocumentStatus(documentId, isProcessing);

  const [displayStatus, setDisplayStatus] = useState(null);
  const currentStatus = displayStatus || doc?.status;
  const isCompleted = currentStatus === 'COMPLETED';

  const {
    data: extractedKpis,
    isLoading: extractedLoading,
    refetch: refetchExtracted,
  } = useExtractedKpis(isCompleted ? documentId : null);

  const {
    data: savedKpis,
    isLoading: savedLoading,
    refetch: refetchSaved,
  } = useSavedKpis(documentId);

  // Whether user has already submitted KPIs
  const hasSavedKpis = !savedLoading && savedKpis.length > 0;
  const kpisNotSavedYet = isCompleted && !savedLoading && savedKpis.length === 0;

  // When user explicitly clicks "Review & Save" from the not-saved banner,
  // force show the KpiReviewTable even if we'd normally hide it.
  const [forceShowReview, setForceShowReview] = useState(false);

  useEffect(() => {
    if (doc?.status) setDisplayStatus(doc.status);
  }, [doc?.status]);

  useEffect(() => {
    if (polledStatus?.status) {
      setDisplayStatus(polledStatus.status);
      if (polledStatus.status === 'COMPLETED') refetchExtracted();
    }
  }, [polledStatus]);

  const handleKpisSaved = () => {
    refetchSaved();
    setForceShowReview(false);
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return d; }
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return '📄';
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return '📄';
    if (['xlsx', 'xls'].includes(ext)) return '📊';
    if (['doc', 'docx'].includes(ext)) return '📝';
    return '📄';
  };

  if (docLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', gap: 16, color: '#6b8794' }}>
        <Spinner animation="border" style={{ color: '#3f88a5' }} />
        <span>Loading document...</span>
      </div>
    );
  }

  const fileName = doc?.fileName || doc?.documentProcessing?.rawFileMetadata?.fileName || 'Unknown Document';
  const showReviewTable = isCompleted && (forceShowReview || kpisNotSavedYet);

  return (
    <div>
      {/* Back */}
      <button
        onClick={onBack}
        style={{
          background: 'none', border: 'none', color: '#3f88a5', fontWeight: 500,
          cursor: 'pointer', padding: 0, marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.875rem',
        }}
        onMouseEnter={(e) => e.currentTarget.style.opacity = 0.7}
        onMouseLeave={(e) => e.currentTarget.style.opacity = 1}
      >
        <i className="fa fa-arrow-left" style={{ fontSize: '0.8rem' }} />
        Back to Documents
      </button>

      {/* Document header card */}
      <div style={{
        background: 'white', border: '1.5px solid #e2eaf0', borderRadius: 12,
        padding: '20px 24px', marginBottom: 20,
        display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: 10, background: '#e8f4f8',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0,
        }}>
          {getFileIcon(fileName)}
        </div>

        <div style={{ flex: 1, minWidth: 200 }}>
          <h5 style={{ margin: 0, fontWeight: 700, color: '#1a2e38', fontSize: '1rem' }}>{fileName}</h5>
          <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap' }}>
            {doc?.financialYear && (
              <span style={{ fontSize: '0.82rem', color: '#6b8794' }}>
                <i className="fa fa-calendar me-1" />FY {doc.financialYear}
              </span>
            )}
            {doc?.locationName && (
              <span style={{ fontSize: '0.82rem', color: '#6b8794' }}>
                <i className="fa fa-map-marker me-1" />
                {doc.locationName}{doc.subLocationName ? ` › ${doc.subLocationName}` : ''}
              </span>
            )}
            <span style={{ fontSize: '0.82rem', color: '#6b8794' }}>
              <i className="fa fa-clock-o me-1" />Uploaded {formatDate(doc?.createdAt)}
            </span>
          </div>
        </div>

        {/* Stats */}
        {(extractedKpis.length > 0 || hasSavedKpis) && (
          <div style={{ display: 'flex', gap: 20 }}>
            {extractedKpis.length > 0 && <StatPill label="Extracted" value={extractedKpis.length} color="#3f88a5" />}
            {hasSavedKpis && <StatPill label="Saved" value={savedKpis.length} color="#27ae60" />}
          </div>
        )}
      </div>

      {/* Processing Tracker */}
      <ProcessingTracker
        status={currentStatus}
        errorMessage={doc?.errorMessage || polledStatus?.errorMessage}
      />

      {/* ── KPIs not saved yet banner ── */}
      {kpisNotSavedYet && !forceShowReview && (
        <div style={{
          background: 'white',
          border: '1.5px solid #f0a500',
          borderLeft: '4px solid #f0a500',
          borderRadius: 12,
          padding: '20px 24px',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: 'rgba(240,165,0,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.2rem', flexShrink: 0,
            }}>
              ⚠️
            </div>
            <div>
              <div style={{ fontWeight: 700, color: '#1a2e38', fontSize: '0.95rem', marginBottom: 3 }}>
                KPIs not saved yet
              </div>
              <div style={{ fontSize: '0.83rem', color: '#6b8794' }}>
                {extractedLoading
                  ? 'Loading extracted KPIs…'
                  : extractedKpis.length > 0
                    ? `${extractedKpis.length} KPI${extractedKpis.length !== 1 ? 's' : ''} were extracted from this document. Review and save them to finalise.`
                    : 'No KPIs were extracted. You can manually add KPIs using the review table.'}
              </div>
            </div>
          </div>
          <button
            onClick={() => setForceShowReview(true)}
            style={{
              background: '#3f88a5', border: 'none', color: 'white',
              fontWeight: 600, fontSize: '0.875rem', borderRadius: 8,
              padding: '9px 20px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
              whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >
            <i className="fa fa-pencil-square-o" />
            Review &amp; Save Now
          </button>
        </div>
      )}

      {/* ── KPIs already saved banner ── */}
      {hasSavedKpis && !forceShowReview && (
        <div style={{
          background: 'white',
          border: '1.5px solid #27ae60',
          borderLeft: '4px solid #27ae60',
          borderRadius: 12,
          padding: '20px 24px',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: 'rgba(39,174,96,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.2rem', flexShrink: 0,
            }}>
              ✅
            </div>
            <div>
              <div style={{ fontWeight: 700, color: '#1a2e38', fontSize: '0.95rem', marginBottom: 3 }}>
                {savedKpis.length} KPI{savedKpis.length !== 1 ? 's' : ''} saved
              </div>
              <div style={{ fontSize: '0.83rem', color: '#6b8794' }}>
                KPIs have been reviewed and saved for this document.
                You can review, edit, and re-save at any time.
              </div>
            </div>
          </div>
          <button
            onClick={() => setForceShowReview(true)}
            style={{
              background: 'transparent',
              border: '1.5px solid #27ae60',
              color: '#27ae60',
              fontWeight: 600, fontSize: '0.875rem', borderRadius: 8,
              padding: '8px 20px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
              whiteSpace: 'nowrap', flexShrink: 0,
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(39,174,96,0.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <i className="fa fa-pencil" />
            Review &amp; Edit
          </button>
        </div>
      )}

      {/* ── Saved KPIs read-only table (shown when saved and not editing) ── */}
      {hasSavedKpis && !forceShowReview && (
        <SavedKpiTable kpis={savedKpis} isLoading={savedLoading} />
      )}

      {/* ── Full KPI review table (edit mode) ── */}
      {showReviewTable && (
        <KpiReviewTable
          documentId={documentId}
          extractedKpis={extractedKpis}
          extractedLoading={extractedLoading}
          savedKpis={savedKpis}
          savedLoading={savedLoading}
          onSaved={handleKpisSaved}
          onCancelReview={hasSavedKpis ? () => setForceShowReview(false) : null}
          refetchExtracted={refetchExtracted}
        />
      )}

      {/* Still processing */}
      {!isCompleted && currentStatus !== 'FAILED' && (
        <div style={{
          background: 'white', border: '1.5px solid #e2eaf0', borderRadius: 12,
          padding: '40px 24px', textAlign: 'center', color: '#6b8794',
        }}>
          <Spinner animation="border" style={{ color: '#3f88a5', marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: '0.9rem' }}>KPIs will appear here once extraction is complete.</p>
        </div>
      )}
    </div>
  );
};

// ── Saved KPIs read-only table ────────────────────────────────────────────────
const SavedKpiTable = ({ kpis, isLoading }) => {
  if (isLoading) {
    return (
      <div style={{ background: 'white', border: '1.5px solid #e2eaf0', borderRadius: 12, padding: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, color: '#6b8794' }}>
        <Spinner animation="border" size="sm" style={{ color: '#3f88a5' }} />
        <span>Loading saved KPIs...</span>
      </div>
    );
  }

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  return (
    <div style={{ background: 'white', border: '1.5px solid #e2eaf0', borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1.5px solid #e2eaf0', display: 'flex', alignItems: 'center', gap: 10 }}>
        <h6 style={{ margin: 0, fontWeight: 700, color: '#1a2e38', fontSize: '0.95rem' }}>Saved KPIs</h6>
        <span style={{ background: 'rgba(39,174,96,0.12)', color: '#27ae60', borderRadius: 6, padding: '2px 8px', fontSize: '0.78rem', fontWeight: 600 }}>
          {kpis.length}
        </span>
      </div>

      {/* Table head */}
      <div style={{
        display: 'grid', gridTemplateColumns: '2.2fr 0.9fr 0.8fr 1.3fr',
        padding: '9px 20px', background: '#f7fafc', borderBottom: '1.5px solid #e2eaf0',
      }}>
        {['KPI', 'Value', 'Unit', 'Period'].map(h => (
          <div key={h} style={{ fontSize: '0.73rem', fontWeight: 700, color: '#6b8794', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</div>
        ))}
      </div>

      {/* Rows */}
      {kpis.map((kpi, idx) => (
        <div key={kpi.id} style={{
          display: 'grid', gridTemplateColumns: '2.2fr 0.9fr 0.8fr 1.3fr',
          padding: '12px 20px', alignItems: 'center',
          borderBottom: idx < kpis.length - 1 ? '1px solid rgba(0,0,0,0.04)' : 'none',
          background: idx % 2 === 0 ? 'white' : '#fafcfd',
        }}>
          <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#1a2e38' }}>{kpi.kpiTitle || '—'}</div>
          <div style={{ fontSize: '0.875rem', fontWeight: 600, color: kpi.value != null ? '#1a2e38' : '#b0c4ce' }}>{kpi.value ?? '—'}</div>
          <div style={{ fontSize: '0.82rem', color: kpi.unit ? '#6b8794' : '#b0c4ce' }}>{kpi.unit || '—'}</div>
          <div style={{ fontSize: '0.8rem', color: '#6b8794' }}>
            {kpi.periodStartDate || kpi.periodEndDate
              ? `${formatDate(kpi.periodStartDate)} – ${formatDate(kpi.periodEndDate)}`
              : '—'}
          </div>
        </div>
      ))}
    </div>
  );
};

const StatPill = ({ label, value, color }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: '1.3rem', fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: '0.72rem', color: '#6b8794', fontWeight: 500, marginTop: 2 }}>{label}</div>
  </div>
);

export default DocumentDetailPage;